import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";
import PersonalNoteModal from "../components/PersonalNoteModal";
import { useBilling } from "../context/BillingContext";
import { Plus, Trash2, Save, Sparkles } from "lucide-react";

const EMPTY = { name:"", company:"", email:"", preferences:"", success_stories:"", last_interaction:"", notes:"" };

export default function Customers() {
  const { label } = useSettings();
  const { handleApiError } = useBilling();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [notePerson, setNotePerson] = useState(null);

  const load = async () => { const { data } = await api.get("/customers"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/customers/${editing.id}`, editing);
      else await api.post("/customers", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { if (!handleApiError(e)) toast.error("Save failed"); }
  };
  const remove = async (id) => { await api.delete(`/customers/${id}`); load(); };

  const inp = "w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm";
  const ta = inp + " min-h-[90px] leading-relaxed";

  return (
    <AppShell eyebrow="Stories" title={`${label("customers")} Database`}
      actions={
        <button onClick={()=>setEditing({ ...EMPTY })} data-testid="customer-new-btn"
          className="bg-[#0033FF] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 inline-flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Add {label("customers").toLowerCase().endsWith("s") ? label("customers").toLowerCase().slice(0,-1) : "customer"}
        </button>
      }>
      <div className="border border-zinc-200">
        {items.length === 0 && <div className="p-8 text-sm text-zinc-500" data-testid="customer-empty">No customers yet. Their stories are the proof that your vision works.</div>}
        {items.map((c) => (
          <div key={c.id} className="border-b border-zinc-200 last:border-0 p-4 flex items-start justify-between gap-4 hover:bg-zinc-50 group" data-testid={`customer-item-${c.id}`}>
            <button onClick={()=>setEditing({ ...EMPTY, ...c })} className="text-left flex-1 min-w-0">
              <div className="text-lg font-bold tracking-tight">{c.name} {c.company && <span className="text-zinc-500 font-normal">· {c.company}</span>}</div>
              {c.success_stories && <div className="text-sm text-zinc-700 mt-1 line-clamp-2"><span className="font-semibold">Story:</span> {c.success_stories}</div>}
              {c.preferences && <div className="text-sm text-zinc-700 line-clamp-1"><span className="font-semibold">Prefs:</span> {c.preferences}</div>}
            </button>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={()=>setNotePerson(c)} data-testid={`customer-compose-${c.id}`} title="Compose personalized note"
                className="border border-zinc-300 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-[#0033FF] hover:text-white hover:border-[#0033FF] inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Note
              </button>
              <button onClick={()=>remove(c.id)} data-testid={`customer-delete-${c.id}`} className="text-zinc-400 hover:text-[#FF3B30] opacity-0 group-hover:opacity-100 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <PersonalNoteModal open={!!notePerson} personKind="customer" person={notePerson} onClose={()=>setNotePerson(null)} />

      {editing && (
        <div className="fixed inset-0 bg-zinc-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={()=>setEditing(null)}>
          <div className="bg-white border border-zinc-200 w-full max-w-2xl p-6" onClick={(e)=>e.stopPropagation()} data-testid="customer-edit-modal">
            <div className="text-xs tracking-[0.25em] uppercase text-[#0033FF] font-bold">Customer</div>
            <h3 className="text-2xl font-black tracking-tighter mt-1 mb-5">{editing.id ? "Edit" : "Add"} customer</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input data-testid="customer-name" className={inp} placeholder="Name" value={editing.name} onChange={(e)=>setEditing({...editing, name: e.target.value})}/>
              <input data-testid="customer-company" className={inp} placeholder="Company" value={editing.company} onChange={(e)=>setEditing({...editing, company: e.target.value})}/>
              <input data-testid="customer-email" className={inp} placeholder="Email" value={editing.email} onChange={(e)=>setEditing({...editing, email: e.target.value})}/>
              <input data-testid="customer-last" className={inp} placeholder="Last interaction (date or note)" value={editing.last_interaction} onChange={(e)=>setEditing({...editing, last_interaction: e.target.value})}/>
            </div>
            <textarea data-testid="customer-prefs" className={ta + " mb-3"} placeholder="Preferences" value={editing.preferences} onChange={(e)=>setEditing({...editing, preferences: e.target.value})}/>
            <textarea data-testid="customer-stories" className={ta + " mb-3"} placeholder="Success stories" value={editing.success_stories} onChange={(e)=>setEditing({...editing, success_stories: e.target.value})}/>
            <textarea data-testid="customer-notes" className={ta} placeholder="Notes" value={editing.notes} onChange={(e)=>setEditing({...editing, notes: e.target.value})}/>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={()=>setEditing(null)} className="border border-zinc-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100">Cancel</button>
              <button onClick={save} data-testid="customer-save" className="bg-[#0033FF] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 inline-flex items-center gap-2">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
